import * as React from 'react';
import { Toast, ToastBody, Toaster, ToastTitle, useId, useToastController } from '@fluentui/react-components';

export type ToastIntent = 'success' | 'error' | 'warning' | 'info';

export interface IAppToast {
    notify: (title: string, options?: { body?: string; intent?: ToastIntent }) => void;
    success: (title: string, body?: string) => void;
    error: (title: string, body?: string) => void;
}

const ToastContext = React.createContext<IAppToast | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const toasterId = useId('app-toaster');
    const { dispatchToast } = useToastController(toasterId);

    const value = React.useMemo<IAppToast>(() => {
        const notify: IAppToast['notify'] = (title, options) => {
            dispatchToast(
                <Toast>
                    <ToastTitle>{title}</ToastTitle>
                    {options?.body && <ToastBody>{options.body}</ToastBody>}
                </Toast>,
                { intent: options?.intent ?? 'info', timeout: options?.intent === 'error' ? 6000 : 3500 },
            );
        };
        return {
            notify,
            success: (title, body) => notify(title, { body, intent: 'success' }),
            error: (title, body) => notify(title, { body, intent: 'error' }),
        };
    }, [dispatchToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <Toaster toasterId={toasterId} position="bottom-end" pauseOnHover />
        </ToastContext.Provider>
    );
};

export function useAppToast(): IAppToast {
    const context = React.useContext(ToastContext);
    if (!context) {
        throw new Error('useAppToast must be used inside <ToastProvider>.');
    }
    return context;
}
